import { ReactNode, useEffect, useRef } from 'react';
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconCircleCheck,
  IconInfoCircle,
} from '@tabler/icons';

import {
  Button,
  Modal,
  ModalBody,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '#root/components/ui';

const variantIcon = {
  success: IconCircleCheck,
  warning: IconAlertTriangle,
  error: IconAlertCircle,
  info: IconInfoCircle,
};

interface AlertDialogProps {
  open: boolean;
  onClose: () => void;
  // Texto en línea bajo el título (sin bloques: va dentro de un <p>)
  message?: string | ReactNode;
  // Contenido con bloques (listas, párrafos); va en el cuerpo con scroll
  children?: ReactNode;
  title?: string;
  variant?: 'error' | 'info' | 'success' | 'warning';
  // sm (400px) para confirmaciones; md (560px) si lleva children largos
  size?: 'sm' | 'md';
  onConfirm?: () => void;
  onCancel?: () => void;
  confirmText?: string | null;
  // Texto del botón secundario; null lo oculta y queda solo el de confirmar
  cancelText?: string | null;
}

/**
 * Confirmación sobre el Modal del DS. Sin X: se cierra con Cancelar, clic afuera o Escape.
 * variant="error" marca la acción como destructiva (botón danger).
 */
const AlertDialog = (props: AlertDialogProps) => {
  const {
    open,
    onClose,
    message,
    children,
    title,
    variant = 'info',
    size = 'sm',
    onConfirm,
    onCancel,
    confirmText = 'Confirmar',
    cancelText = 'Cancelar',
  } = props;
  const Icon = variantIcon[variant];
  const initialFocusRef = useRef<HTMLButtonElement>(null);

  // El foco inicial va en Cancelar (o en Confirmar si es el único botón); Tab pasa al otro.
  // No sirve autoFocus: al abrir desde un Menu/Drawer de MUI, este devuelve el foco a su
  // ítem en el mismo commit y luego el TrapFocus del Modal lo manda al contenedor.
  useEffect(() => {
    if (!open) return undefined;
    const timer = window.setTimeout(() => initialFocusRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [open]);

  return (
    <Modal open={open} onClose={onClose} size={size} hideClose>
      <ModalHeader icon={<Icon />} tone={variant}>
        <ModalTitle>{title}</ModalTitle>
        {message && <ModalDescription>{message}</ModalDescription>}
      </ModalHeader>
      {children && <ModalBody>{children}</ModalBody>}
      <ModalFooter>
        {cancelText !== null && (
          <Button ref={initialFocusRef} variant="ghost" onClick={onCancel ?? onClose}>
            {cancelText}
          </Button>
        )}
        <Button
          ref={cancelText === null ? initialFocusRef : undefined}
          variant={variant === 'error' ? 'danger' : 'primary'}
          onClick={onConfirm}>
          {confirmText}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AlertDialog;
